import { useState } from "react";
import { useApiTokens } from "../hooks/useApiTokens";
import { cn } from "@/shared/lib/utils";
import {
  KeyIcon,
  PlusIcon,
  TrashIcon,
  ClipboardDocumentIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { toast } from "sonner";

interface ApiTokensPanelProps {
  className?: string;
}

export function ApiTokensPanel({ className }: ApiTokensPanelProps) {
  const {
    tokens,
    isLoading,
    isCreating,
    isRevokingToken,
    createToken,
    revokeToken,
    newToken,
    closeNewTokenModal,
  } = useApiTokens();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [tokenName, setTokenName] = useState("");

  const handleCreateToken = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tokenName.trim()) return;

    try {
      await createToken(tokenName.trim());
      setTokenName("");
      setIsCreateModalOpen(false);
    } catch {
      // Error handled by hook
    }
  };

  const handleCopyToken = (token: string) => {
    navigator.clipboard.writeText(token);
    toast.success("Token copied to clipboard");
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const activeTokens = tokens.filter((t) => !t.revokedAt);
  const revokedTokens = tokens.filter((t) => t.revokedAt);

  return (
    <div className={cn("space-y-6", className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">API Tokens</h2>
          <p className="text-muted-foreground mt-1">
            Manage your API tokens for integrating with agent runners like Claude Code, Cursor, and others.
          </p>
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
        >
          <PlusIcon className="h-5 w-5" />
          Create Token
        </button>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <KeyIcon className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-medium text-blue-900">About API Tokens</p>
            <p className="text-blue-700 mt-1">
              API tokens allow agent runners to access your Refstash projects and references.
              Store them securely in environment variables (e.g., <code className="px-1 py-0.5 bg-blue-100 rounded">REFSTASH_API_TOKEN</code>).
            </p>
          </div>
        </div>
      </div>

      {/* Active Tokens */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Active Tokens ({activeTokens.length})</h3>

        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="h-24 bg-muted animate-pulse rounded-lg" />
            ))}
          </div>
        ) : activeTokens.length === 0 ? (
          <div className="text-center py-12 border border-dashed rounded-lg">
            <KeyIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No active tokens</p>
            <p className="text-sm text-muted-foreground mt-1">
              Create a token to integrate with agent runners
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {activeTokens.map((token) => (
              <div
                key={token._id}
                className="bg-card border rounded-lg p-4 hover:border-muted-foreground/20 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold">{token.name}</h4>
                      <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">
                        Active
                      </span>
                    </div>
                    <div className="mt-2 text-sm text-muted-foreground space-y-1">
                      <p>Created: {formatDate(token._creationTime)}</p>
                      {token.lastUsedAt && (
                        <p>Last used: {formatDate(token.lastUsedAt)}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      if (confirm(`Are you sure you want to revoke "${token.name}"?`)) {
                        revokeToken(token._id);
                      }
                    }}
                    disabled={isRevokingToken === token._id}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                  >
                    <TrashIcon className="h-4 w-4" />
                    {isRevokingToken === token._id ? "Revoking..." : "Revoke"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Revoked Tokens */}
      {revokedTokens.length > 0 && (
        <details className="space-y-4">
          <summary className="text-lg font-semibold cursor-pointer hover:text-muted-foreground transition-colors">
            Revoked Tokens ({revokedTokens.length})
          </summary>
          <div className="space-y-3 pl-4">
            {revokedTokens.map((token) => (
              <div
                key={token._id}
                className="bg-muted/50 border border-dashed rounded-lg p-4 opacity-75"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-muted-foreground">{token.name}</h4>
                      <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                        Revoked
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Revoked: {token.revokedAt ? formatDate(token.revokedAt) : "Unknown"}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Create Token Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4">
          <div className="bg-base-100 text-base-content rounded-lg shadow-xl border-2 border-base-300 max-w-md w-full p-6 animate-in fade-in-0 duration-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Create API Token</h3>
              <button
                onClick={() => setIsCreateModalOpen(false)}
                className="p-1 hover:bg-muted rounded-lg transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleCreateToken} className="space-y-4">
              <div>
                <label htmlFor="tokenName" className="block text-sm font-medium mb-1">
                  Token Name
                </label>
                <input
                  id="tokenName"
                  type="text"
                  value={tokenName}
                  onChange={(e) => setTokenName(e.target.value)}
                  placeholder="e.g., Claude Code, Cursor, VS Code"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  autoFocus
                />
                <p className="text-xs text-muted-foreground mt-1">
                  A descriptive name to help you identify this token
                </p>
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setIsCreateModalOpen(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-muted transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!tokenName.trim() || isCreating}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {isCreating ? "Creating..." : "Create Token"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Token Display Modal */}
      {newToken && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4">
          <div className="bg-base-100 text-base-content rounded-lg shadow-xl border-2 border-base-300 max-w-lg w-full p-6 animate-in fade-in-0 duration-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Your API Token</h3>
              <button
                onClick={closeNewTokenModal}
                className="p-1 hover:bg-muted rounded-lg transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <p className="text-sm text-orange-900 font-medium">Copy this token now</p>
                <p className="text-sm text-orange-700 mt-1">
                  You won't be able to see it again. Store it securely.
                </p>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium">Token</label>
                <div className="flex gap-2">
                  <code className="flex-1 px-3 py-2 bg-muted rounded-lg text-sm font-mono break-all select-all">
                    {newToken.token}
                  </code>
                  <button
                    onClick={() => handleCopyToken(newToken.token)}
                    className="px-3 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    <ClipboardDocumentIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium">Example Usage</label>
                <div className="bg-muted rounded-lg p-3 text-sm font-mono">
                  <p className="text-muted-foreground"># Set environment variable</p>
                  <p>export REFSTASH_API_TOKEN="{newToken.token}"</p>
                  <p className="text-muted-foreground mt-2"># Or in .env file</p>
                  <p>REFSTASH_API_TOKEN={newToken.token}</p>
                </div>
              </div>
              <button
                onClick={closeNewTokenModal}
                className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
              >
                I've copied my token
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
